// ═══════════════════════════════════════════════════════════════════════════════
// POST /api/inventory/upload-stocktake & analyze-stocktake
// ═══════════════════════════════════════════════════════════════════════════════

import {
  AI_ANALYZE_FETCH_TIMEOUT_MS,
  AI_MAX_RETRY_COUNT,
  AI_RETRY_WAIT_MS,
} from '@fdh/shared';
import { getStoredToken } from './api';
import { emitLimitReachedFromHttp } from './limitError';

export type InventoryStocktakeAnalyzeData = {
  mode?: 'catalog' | 'discovery';
  storedImageUrl?: string;
  storedImageUrlForDisplay?: string;
  matches: { itemId: string; status: string; confidence: number }[];
  notFoundItemIds: string[];
  discoveryAssignments?: {
    categoryName: string;
    items: {
      itemName: string;
      itemNameEmployer?: string;
      status: string | null;
      confidence: number;
    }[];
  }[];
  rawText: string;
  analysisOk: boolean;
  analysisError?: string;
  /** Backend skips Gemini when daily inventory AI quota is exhausted; upload may still succeed. */
  analysisSkippedReason?: 'daily_ai_limit';
};

export type InventoryStocktakeUploadData = {
  storedImageUrl?: string;
  storedImageUrlForDisplay?: string;
};

export class InventoryStocktakeHttpError extends Error {
  readonly status: number;

  readonly body: Record<string, unknown>;

  constructor(status: number, body: Record<string, unknown>) {
    super(typeof body.error === 'string' ? body.error : `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const isRetriableNetworkFailure = (e: unknown): boolean => {
  if (e instanceof TypeError) return true;
  if (e instanceof DOMException && e.name === 'AbortError') return true;
  return false;
};

const postStocktakeFormOnce = async <T>(path: string, formData: FormData): Promise<T> => {
  const token = getStoredToken();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), AI_ANALYZE_FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(path, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: controller.signal,
    });

    const json = (await response.json()) as Record<string, unknown>;

    if (!response.ok) {
      emitLimitReachedFromHttp(response.status, json);
      throw new InventoryStocktakeHttpError(response.status, json);
    }

    if (json.success !== true || json.data === undefined || json.data === null) {
      throw new Error(typeof json.error === 'string' ? json.error : 'Request failed');
    }

    return json.data as T;
  } finally {
    clearTimeout(timeoutId);
  }
};

const postStocktakeFormWithRetries = async <T>(path: string, formData: FormData): Promise<T> => {
  const maxAttempts = AI_MAX_RETRY_COUNT + 1;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (attempt > 0) {
      const waitMs = AI_RETRY_WAIT_MS[attempt - 1] ?? 1000;
      await sleep(waitMs);
    }
    try {
      return await postStocktakeFormOnce<T>(path, formData);
    } catch (e) {
      if (e instanceof InventoryStocktakeHttpError) {
        throw e;
      }
      if (!isRetriableNetworkFailure(e) || attempt === maxAttempts - 1) {
        throw e;
      }
    }
  }
  throw new Error('Request failed');
};

export const uploadInventoryStocktake = async (
  blob: Blob,
  fileName: string,
  instanceId: string,
  options?: { locationName?: string }
): Promise<InventoryStocktakeUploadData> => {
  const fd = new FormData();
  fd.append('file', blob, fileName);
  fd.append('instanceId', instanceId);
  const locationName = options?.locationName?.trim() ?? '';
  if (locationName !== '') {
    fd.append('locationName', locationName);
  }
  return postStocktakeFormWithRetries<InventoryStocktakeUploadData>('/api/inventory/upload-stocktake', fd);
};

const analyzeOneStocktake = async (
  instanceId: string,
  options: { locationName?: string; blob?: Blob; fileName?: string; existingCanonicalUrl?: string }
): Promise<InventoryStocktakeAnalyzeData> => {
  const fd = new FormData();
  fd.append('instanceId', instanceId);
  const locationName = options.locationName?.trim() ?? '';
  if (locationName !== '') {
    fd.append('locationName', locationName);
  }
  const existing = options.existingCanonicalUrl?.trim() ?? '';
  if (existing !== '') {
    fd.append('existingCanonicalUrl', existing);
  } else if (options.blob && options.fileName) {
    fd.append('file', options.blob, options.fileName);
  } else {
    throw new Error('Analyze requires file or existingCanonicalUrl');
  }
  return postStocktakeFormWithRetries<InventoryStocktakeAnalyzeData>('/api/inventory/analyze-stocktake', fd);
};

export type StocktakeAnalyzeTarget =
  | { kind: 'pending'; blob: Blob; fileName: string }
  | { kind: 'stored'; canonicalUrl: string };

/** @deprecated Use uploadInventoryStocktake + analyzeSelectedStocktakePhotos */
export const analyzeInventoryStocktakeSequential = async (
  items: Array<{ blob: Blob; fileName: string }>,
  instanceId: string,
  onProgress?: (current: number, total: number) => void,
  options?: { locationName?: string }
): Promise<InventoryStocktakeAnalyzeData[]> => {
  const targets: StocktakeAnalyzeTarget[] = items.map((item) => ({
    kind: 'pending' as const,
    blob: item.blob,
    fileName: item.fileName,
  }));
  return analyzeSelectedStocktakePhotos(targets, instanceId, onProgress, options);
};

export const analyzeSelectedStocktakePhotos = async (
  targets: StocktakeAnalyzeTarget[],
  instanceId: string,
  onProgress?: (current: number, total: number) => void,
  options?: { locationName?: string }
): Promise<InventoryStocktakeAnalyzeData[]> => {
  const out: InventoryStocktakeAnalyzeData[] = [];
  const total = targets.length;
  const locationName = options?.locationName?.trim() ?? '';

  for (let i = 0; i < targets.length; i += 1) {
    onProgress?.(i + 1, total);
    const target = targets[i];
    const part =
      target.kind === 'stored'
        ? await analyzeOneStocktake(instanceId, {
            locationName,
            existingCanonicalUrl: target.canonicalUrl,
          })
        : await analyzeOneStocktake(instanceId, {
            locationName,
            blob: target.blob,
            fileName: target.fileName,
          });
    out.push(part);
  }

  return out;
};
